"use client";

import { X } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { LanguageSwitcher } from "~/components/language-switcher";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import type { SiteLocale } from "~/lib/graphql";

interface NavigationItem {
  slug: string;
  shortTitle: string;
}

interface MobileNavigationProps {
  navigationItems: NavigationItem[];
  isOpen: boolean;
  onClose: () => void;
  locale?: SiteLocale;
  availableLocales?: SiteLocale[];
  currentSlug?: string;
  allSlugLocales?: Array<{
    locale: string;
    value: string;
  }>;
}

export function MobileNavigation({
  navigationItems,
  isOpen,
  onClose,
  locale,
  availableLocales,
  currentSlug,
  allSlugLocales,
}: MobileNavigationProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const handleLinkClick = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-top-0 top-0 h-auto w-full max-w-full translate-y-0 rounded-none border-0 bg-white p-6 pb-20">
        <VisuallyHidden>
          <DialogTitle>Navigation Menu</DialogTitle>
        </VisuallyHidden>

        <div className="mt-1 mb-8 flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="border-eurac-grey-60 h-8 w-8 rounded-full border [&_svg]:size-6"
          >
            <X className="text-eurac-grey-60 h-10 w-10" />
          </Button>
        </div>

        <div className="flex flex-col items-end space-y-6">
          {navigationItems.map((item) => (
            <Link
              key={item.slug}
              href={`#${item.slug}`}
              onClick={handleLinkClick}
              className="hover:text-landing-400 text-landing-900 px-2 py-1 text-base font-medium transition-colors"
            >
              {item.shortTitle}
            </Link>
          ))}

          {/* Language Switcher in Mobile */}
          {locale && availableLocales && availableLocales.length > 1 && (
            <div className="mt-4">
              <LanguageSwitcher
                currentLang={locale}
                availableLocales={availableLocales}
                currentSlug={currentSlug}
                allSlugLocales={allSlugLocales}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
